import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import {
  type Company,
  type CompanyId,
  CompanyPartnership,
  type CompanyPdrn,
} from 'common/api/api-types';
import { useAuthState } from 'common/utils/authenticatorUtils';

import useCompaniesByUserId from './useCompaniesByUserId';
import useCompanyByCompanyId from './useCompanyByCompanyId';

const useCurrentCompany = () => {
  const { user } = useAuthState();
  const [searchParams] = useSearchParams();
  const rawSearchCompanyId = searchParams.get('companyId');
  const companiesByUser = useCompaniesByUserId({ userId: user?.id });

  const searchCompanyId: CompanyId | undefined = rawSearchCompanyId
    ? companiesByUser.data?.find(({ companyId }) => companyId === rawSearchCompanyId)?.companyId
    : undefined;

  const companyId: CompanyId | undefined =
    searchCompanyId ?? companiesByUser.data?.at(0)?.companyId;

  const companyPdrn: CompanyPdrn | undefined = companiesByUser.data?.find(
    c => c.companyId === companyId,
  )?.companyPdrn;
  const LCSP_COMPANY_PARTNERSHIP = CompanyPartnership.enum['lcsp-company'];
  const SUBSTITUTION_PARTNERSHIP = CompanyPartnership.enum.substitution;

  const selectCompanyDataFn = useCallback(
    (data: Company) => {
      const partnership = data.partnership;
      const isCurrentCompanyPartnershipAvailable =
        partnership !== undefined && partnership !== null;
      const isCurrentCompanyPartnershipLCSP = partnership === LCSP_COMPANY_PARTNERSHIP;
      const isCurrentCompanyPartnershipSubstitution = partnership === SUBSTITUTION_PARTNERSHIP;

      return {
        ...data,
        isCurrentCompanyPartnershipAvailable,
        isCurrentCompanyPartnershipLCSP,
        isCurrentCompanyPartnershipSubstitution,
        companyPdrn,
      };
    },
    [LCSP_COMPANY_PARTNERSHIP, SUBSTITUTION_PARTNERSHIP, companyPdrn],
  );

  return {
    currentCompany: useCompanyByCompanyId({ companyId, select: selectCompanyDataFn }),
    companiesByUser,
  };
};

export default useCurrentCompany;
